import { useNavigate } from "react-router";

import { t } from "i18next";

import { Box, Button, Typography } from "@mui/material";

import {
  type Notification,
  type NotificationSection
} from "@/src/types/notification";

const renderCustomTypography = (text: string | undefined) => {
  const textParts = text?.split("\n\n") || [];
  const title = textParts[0];
  const body = textParts.slice(1).join("\n\n");

  return (
    <>
      {title && (
        <Typography
          variant="body1"
          sx={{
            fontWeight: "fontWeightSemibold",
            lineHeight: "150%",
            letterSpacing: "0.25px",
            mb: body ? 0.5 : 0
          }}
        >
          {title}
        </Typography>
      )}
      {body && (
        <Typography
          variant="body2"
          sx={{
            lineHeight: "143%",
            letterSpacing: "0.25px"
          }}
        >
          {body}
        </Typography>
      )}
    </>
  );
};

const renderSections = (sections: NotificationSection[]) =>
  sections.map((section) => (
    <Box key={section.title} sx={{ mt: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: "fontWeightSemibold" }}>
        {section.title}
      </Typography>
      <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
        {section.items.map((item) => (
          <Typography
            key={item}
            component="li"
            variant="body2"
            sx={{ overflowWrap: "anywhere" }}
          >
            {item}
          </Typography>
        ))}
      </Box>
    </Box>
  ));

interface CustomToastProps {
  notification: Notification;
  closeToast?: () => void;
}

const CustomToast = ({ notification, closeToast }: CustomToastProps) => {
  const navigate = useNavigate();

  const handleLinkClick = () => {
    if (notification.auctionId !== undefined) {
      if (closeToast) closeToast();
      navigate(`/dealer/auction/${notification.auctionId}`);
    } else if (notification.linkText !== undefined) {
      if (closeToast) closeToast();
      const url = notification.linkText;
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const hasLink =
    notification.auctionId !== undefined || notification.linkText !== undefined;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%"
      }}
    >
      <Box sx={{ width: "100%", textAlign: "left" }}>
        {notification?.useCustomTypography ? (
          renderCustomTypography(notification.text)
        ) : (
          <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
            {notification?.text}
          </Typography>
        )}
        {notification.sections?.length
          ? renderSections(notification.sections)
          : null}
      </Box>
      {hasLink && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            width: "100%",
            mt: 1.5
          }}
        >
          <Button
            variant="text"
            onClick={handleLinkClick}
            aria-label={
              notification.auctionId !== undefined
                ? String(t("components.notificationContainer.showAuction"))
                : String(t("components.notificationContainer.clickHere"))
            }
            sx={{
              // Inherits the toast label colour: white on the light mode filled
              // toast, Alert/Content on the dark mode outlined one.
              color: "inherit",
              fontWeight: "fontWeightSemibold",
              textTransform: "uppercase",
              py: 0.75,
              px: 1.5,
              textDecoration: "underline",
              textUnderlineOffset: "4px",
              "&:hover": {
                backgroundColor: "action.hover",
                textDecoration: "underline"
              }
            }}
          >
            {notification.auctionId !== undefined
              ? t("components.notificationContainer.showAuction")
              : t("components.notificationContainer.clickHere")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CustomToast;
